import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { notificationStrings } from '../constants/strings';
import RequestRepository from '../repositories/requestRepository';
import { useZustand } from '../zustandStore';

import { requestKeys } from './query-keys';

const logger = require('pino')();

export const useGetRequestListQuery = () => {
	return useQuery({
		queryKey: requestKeys.getRequestList(),
		queryFn: () => RequestRepository.getRequestList(),
		staleTime: Number.POSITIVE_INFINITY, // CHECK: is this the right way to do this?
	});
};

export const getRequestQuery = (requestId: string) => ({
	queryKey: [requestKeys.getRequest, requestId],
	queryFn: () => RequestRepository.getRequest({ requestId }),
	enabled: Boolean(requestId),
});

export const useAddTagMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (options: { requestId: string; tag: string }) => RequestRepository.addTag(options),
		onMutate: async ({ requestId }) => {
			await queryClient.cancelQueries({ queryKey: [requestKeys.getRequest, requestId] });

			const previousRequestData: any = queryClient.getQueryData([requestKeys.getRequest, requestId]);

			if (previousRequestData) {
				const updatedData = { ...previousRequestData, isArchived: true };
				queryClient.setQueryData([requestKeys.getRequest, requestId], updatedData);
			}

			return { previousRequestData };
		},
		onError: (err, variables, context: any) => {
			logger.error(err);
			if (context?.previousRequestData) {
				queryClient.setQueryData([requestKeys.getRequest, variables.requestId], context.previousRequestData);
			}
		},
		onSettled: async (_data, error, variables) => {
			if (error) {
				logger.error({ error });
			}
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: [requestKeys.getRequest, variables.requestId],
				}),
				queryClient.invalidateQueries({
					queryKey: requestKeys.getRequestList(),
				}),
			]);
		},
	});
};

export const useRemoveTagMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (options: { requestId: string; tag: string }) => RequestRepository.removeTag(options),
		onMutate: async ({ requestId }) => {
			const previousRequestData: any = queryClient.getQueryData([requestKeys.getRequest, requestId]);

			if (previousRequestData) {
				const updatedData = { ...previousRequestData, isArchived: false };
				queryClient.setQueryData([requestKeys.getRequest, requestId], updatedData);
			}

			return { previousRequestData };
		},
		onSettled: (_data, _error, variables) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: [requestKeys.getRequest, variables.requestId],
				}),
				queryClient.invalidateQueries({
					queryKey: requestKeys.getRequestList(),
				}),
			]),
		onError: (err, variables, context: any) => {
			logger.error(err);

			if (context?.previousRequestData) {
				queryClient.setQueryData([requestKeys.getRequest, variables.requestId], context.previousRequestData);
			}
		},
	});
};

export const useCancelRequestMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (options: { requestId: string }) => RequestRepository.cancelRequest(options),
		onMutate: async ({ requestId }) => {
			await queryClient.cancelQueries({ queryKey: [requestKeys.getRequest, requestId] });

			const previousRequestData: any = queryClient.getQueryData([requestKeys.getRequest, requestId]);

			if (previousRequestData) {
				const updatedData = { ...previousRequestData, status: 'canceled' };
				queryClient.setQueryData([requestKeys.getRequest, requestId], updatedData);
			}

			return { previousRequestData };
		},
		onError: (err, variables, context: any) => {
			logger.error(err);
			if (context?.previousRequestData) {
				queryClient.setQueryData([requestKeys.getRequest, variables.requestId], context.previousRequestData);
			}
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({ queryKey: [requestKeys.getRequest, variables.requestId] });
		},
	});
};

export const useSubmitRequestMutation = () => {
	const queryClient = useQueryClient();
	const { setNotification } = useZustand();

	return useMutation({
		mutationFn: (options: { requestId: string; data: any }) => RequestRepository.submitRequest(options),
		onMutate: async ({ requestId }) => {
			await queryClient.cancelQueries({ queryKey: [requestKeys.getRequest, requestId] });

			const previousRequestData: any = queryClient.getQueryData([requestKeys.getRequest, requestId]);

			if (previousRequestData) {
				const updatedData = { ...previousRequestData, status: 'submitted' };
				queryClient.setQueryData([requestKeys.getRequest, requestId], updatedData);
			}

			return { previousRequestData };
		},
		onError: (error: any, variables, context: any) => {
			logger.error({ error });

			if (error?.status === 429) {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message:
							'Your company has exceeded the limit for card requests submitted per minute. Please limit requests to less than 5 per minute and try again later.',
					},
				]);
			} else {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message: 'There was a problem submitting the purchase request.',
					},
				]);
			}

			if (context?.previousRequestData) {
				queryClient.setQueryData([requestKeys.getRequest, variables.requestId], context.previousRequestData);
			}
		},
		onSettled: (_data, error) => {
			if (error) {
				logger.error({ error });
			}
			queryClient.invalidateQueries({ type: 'active' });
		},
	});
};

export const useRegisterRequestMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (options: { requestId: string }) => RequestRepository.registerRequest(options),
		onSettled: (_data, _error, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [requestKeys.getRequest, variables.requestId],
			});
			// we invalidate the cache so we get the latest data so we know
			// if we have already created a new request in CREATED state
			void queryClient.invalidateQueries({ queryKey: [requestKeys.getRequestList] });
		},
		onError: (err, variables, context: any) => {
			logger.error(err);

			if (context?.previousRequestData) {
				queryClient.setQueryData([requestKeys.getRequest, variables.requestId], context.previousRequestData);
			}
		},
	});
};
